<template>
  <div class="living-people-editor">
    <div class="group-by">
      <div>Show relationship to:</div>
      <SourcePersonSelector v-model="startPersonIdRef" :session-id="sessionId"/>
    </div>
    <ul class="list-unstyled living-people-list">
      <template v-if="startPersonIdRef">
        <li v-for="rc in categoriesRef" :key="rc">
          <LivingPeopleCategory
            :category="rc"
            :persons="livingWindowGroupsRef.get(rc) ?? []"
            :session-id="sessionId"/>
        </li>
      </template>
      <template v-else>
        <li v-for="p in otherPersonsRef" :key="p.id">
          <LivingPersonListItem :person="p" :session-id="sessionId"/>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.living-people-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .group-by {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-size: 0.875rem;
    align-items: flex-start;

    .source-person-selector {
      width: 100%;
      max-width: 300px;
    }

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
    }
  }
}

.living-people-list {
  margin: 0;
  margin-left: 1rem;
  height: 250px;
  max-height: 250px;
  overflow-y: auto;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { Person } from '@webapp/rd/ResearchDataModel'
import { useSyncSessionStore } from './SyncSessionStore'
import { RelationshipCategory, orderedRelationshipCategories, buildLivingWindowGroups } from './LivingWindowGroups'
import LivingPeopleCategory from './LivingPeopleCategory.vue'
import LivingPersonListItem from './LivingPersonListItem.vue'
import SourcePersonSelector from './SourcePersonSelector.vue'

const props = defineProps<{
  sessionId: string
}>()

const syncSessionStore = useSyncSessionStore()
const stateRef = computed(() => syncSessionStore.getState(props.sessionId))

const startPersonIdRef = computed({
  get: () => stateRef.value?.livingStartPersonId,
  set: (value) => {
    const state = stateRef.value
    if (state) {
      state.livingStartPersonId = value
    }
  },
})

const livingWindowGroupsRef = computed(() => {
  const graph = stateRef.value?.sourceGraph
  return graph
    ? buildLivingWindowGroups(graph, stateRef.value?.livingStartPersonId)
    : new Map<RelationshipCategory, Person[]>()
})

const categoriesRef = computed(() => orderedRelationshipCategories.filter(rc =>
  rc != RelationshipCategory.Ancestor ||
  (livingWindowGroupsRef.value.get(rc) ?? []).length > 0))

const otherPersonsRef = computed(() => livingWindowGroupsRef.value.get(RelationshipCategory.Other) ?? [])
</script>
